import {
    useEffect,
    useState,
} from 'react';

// FNP-918: module-scoped filter store (checklist §7; FNP-815).
//
// Filters live here rather than in component state so the FilterDrawer and the
// table filter the SAME list. Keyword and filters COMPOSE — they are applied
// independently in useSubscriptions, so applying both narrows rather than
// resets.
//
// ⚠️ These filters are applied CLIENT-SIDE, over the pages loaded so far.
// `listSubscriptions` does accept a `filter` argument, but the schema's
// SubscriptionsFilterInput is used here only for `customerId` (see
// useSubscriptionsQuery) — status is not a server-side filter field, so the
// drawer narrows the loaded window. See the comment in useSubscriptions.ts.
export type SubscriptionsFilters = Record<string, string>;

let globalFilters: SubscriptionsFilters = {};
const filterListeners: Set<(filters: SubscriptionsFilters) => void> = new Set();

const notifyFilterChange = (filters: SubscriptionsFilters) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

/** Test seam: resets the store so filters do not leak between test cases. */
export const resetSubscriptionsFilter = () => notifyFilterChange({});

export const useSubscriptionsFilter = () => {
    const [filters, setFilters] = useState<SubscriptionsFilters>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: SubscriptionsFilters) =>
            setFilters(newFilters);
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    // The badge on FilterRecordsButton — a filter you cannot see is a filter you
    // forget is on (§7). Empty-string values are treated as unset so a cleared
    // field does not keep inflating the count.
    const activeFilterCount = Object.values(filters)
                                    .filter(value => value !== '' && value !== undefined)
                                    .length;

    return {
        filters,
        activeFilterCount,
        handleSetFilters: notifyFilterChange,
        handleClearFilters: () => notifyFilterChange({}),
    };
};
